import { useMemo, useState } from "react";
import { http, toastApiError, downloadUrl } from "../lib/api";
import { toast } from "../lib/notify";
import SearchableSelect from "./SearchableSelect";
import { PackageCheck, X, FileText } from "lucide-react";

const HEADERS = ["Demirbaş Adı", "Cinsi", "Seri No", "Durum"];
const personLabel = (p) => `${p.tc_no || "—"} - ${p.full_name || ""}${p.project_name ? ` (${p.project_name})` : ""}`;

function AssetPickTable({ free, sel, setSel, toggle }) {
  return (
    <div className="border border-slate-200 rounded-md max-h-72 overflow-auto">
      <table className="w-full tbl">
        <thead className="bg-slate-50 sticky top-0"><tr><th className="px-2 py-1.5 w-8"><input type="checkbox" data-testid="bulk-assign-all" checked={free.length > 0 && sel.length === free.length} onChange={(e) => setSel(e.target.checked ? free.map((a) => a.id) : [])} /></th>{HEADERS.map((h) => <th key={h} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {free.map((a) => (
            <tr key={a.id} className={`border-t border-slate-100 cursor-pointer ${sel.includes(a.id) ? "bg-indigo-50" : "hover:bg-slate-50"}`} onClick={() => toggle(a.id)} data-testid={`bulk-assign-row-${a.id}`}>
              <td className="px-2 py-1.5"><input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} onClick={(e) => e.stopPropagation()} data-testid={`bulk-assign-check-${a.id}`} /></td>
              <td className="px-2 py-1.5 text-xs font-semibold">{a.name}</td>
              <td className="px-2 py-1.5 text-xs">{a.kind || "—"}</td>
              <td className="px-2 py-1.5 text-xs font-mono">{a.serial_no || "—"}</td>
              <td className="px-2 py-1.5 text-xs">{a.assignee_personnel_id ? <span className="text-sky-700">Bu personelde</span> : <span className="text-slate-500">Boşta</span>}</td>
            </tr>
          ))}
          {free.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-xs text-slate-500">Zimmetlenebilecek boş demirbaş yok</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BulkAssignResult({ result }) {
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-assign-result">
      <div className="text-sm text-emerald-900 font-semibold">{result.assigned.length} demirbaş zimmetlendi.</div>
      <a href={downloadUrl(result.tutanak_url)} target="_blank" rel="noreferrer" data-testid="bulk-assign-tutanak" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-rose-700 text-white text-xs font-semibold hover:bg-rose-800"><FileText className="w-3.5 h-3.5" /> Tek Tutanak PDF</a>
    </div>
  );
}

export default function AssetBulkAssign({ assets, personnel, onDone, onClose }) {
  const [pid, setPid] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const people = useMemo(() => personnel.filter((p) => p.status !== "Pasif").sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "", "tr")).map((p) => ({ value: p.id, label: personLabel(p) })), [personnel]);
  const free = useMemo(() => assets.filter((a) => !a.assignee_personnel_id || a.assignee_personnel_id === pid), [assets, pid]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const submit = async () => {
    if (!pid) return toast.error("Personel seçin");
    if (!sel.length) return toast.error("En az bir demirbaş seçin");
    setBusy(true);
    try {
      const { data } = await http.post("/fixed-assets/bulk-assign", { personnel_id: pid, asset_ids: sel, assigned_at: date || null });
      setResult(data);
      toast.success(`${data.assigned.length} demirbaş zimmetlendi`);
      onDone();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-indigo-300 rounded-lg p-5 space-y-4" data-testid="asset-bulk-assign">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-indigo-700 inline-flex items-center gap-2"><PackageCheck className="w-4 h-4" /> Toplu Zimmet</div>
        <button type="button" onClick={onClose} data-testid="bulk-assign-close" className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
      </div>
      {result ? <BulkAssignResult result={result} /> : (<>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block md:col-span-2"><span className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Zimmet Alan Personel</span><SearchableSelect testid="bulk-assign-person" value={pid} onChange={(v) => { setPid(v || ""); setSel([]); }} options={people} placeholder="Personel ara…" /></label>
          <label className="block"><span className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Zimmet Tarihi</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-assign-date" className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md" /></label>
        </div>
        <AssetPickTable free={free} sel={sel} setSel={setSel} toggle={toggle} />
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600" data-testid="bulk-assign-count">{sel.length} demirbaş seçildi</span>
          <button type="button" onClick={submit} disabled={busy || !pid || !sel.length} data-testid="bulk-assign-submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-700 text-white text-xs font-semibold hover:bg-indigo-800 disabled:opacity-50"><PackageCheck className="w-4 h-4" /> {busy ? "Zimmetleniyor…" : "Zimmetle ve Tutanak Oluştur"}</button>
        </div>
      </>)}
    </div>
  );
}
